import Resource from './Resource';
import Attributes from 'Attributes/SoliditetClientAttributes';
import AppService from 'App/legacy/AppService';
import EventBus from 'App/services/EventBus';
import { parseDates } from 'App/resources/genericParser';
import { parseCategories } from 'App/resources/parsers/client';
import { parseCustom } from 'App/resources/parsers/helpers';
import T from 'Components/Helpers/translate';

/*
	Standalone SoliditetClient (WEB-8568). The Angular factory built a per-customer $resource with
	actions spread over three URL roots, so customer(id) here returns a small facade that passes an
	explicit _url per call rather than pretending one base fits all.

	Only the seven members React uses are ported — find, findMatches, searchMatches, buyPreview, buy,
	refresh, updateMatches — plus attr. preview, get and pending have no consumer outside the Angular
	tree and stay on the factory.
*/

/*
	Port of upsales/common/resources/parsers/account.js, which buy and refresh responses ran through
	via ParseGeneric('ParseAccount'). Deliberately not resources/parsers/client.ts: that one omits
	parseAddresses and drops 'Contact' from the createRights OWN list, so reusing it would change
	both the $addresses map and contact-create permissions on a freshly bought account.
*/
const parseAccount = (account: any) => {
	const self = AppService.getSelf();
	const categories = AppService.getCategories('account');
	const categoryTypes = AppService.getCategoryTypes('account');

	// ParseGeneric ran parseDates and parseCustom before handing the account to ParseAccount, and
	// the result is broadcast as account.added/account.updated, so listeners expect both.
	account = parseDates(account);
	if (account.custom) {
		account.$mappedCustom = parseCustom(account.custom, 'account');
	}

	parseCategories(account, categories, categoryTypes);

	account.$addresses = {};
	if (Object.prototype.hasOwnProperty.call(account, 'addresses')) {
		(account.addresses ?? []).forEach((address: { type: string }) => {
			account.$addresses[address.type] = address;
		});
	}

	account.$adCampaign = {};
	if (account.adCampaign && account.adCampaign.length) {
		for (let i = 0; i < account.adCampaign.length; i++) {
			account.$adCampaign[account.adCampaign[i].id] = account.adCampaign[i];
		}
	}

	const accountOwner = (account.users ?? []).some((user: { id: number }) => user.id === self.id);

	const createRights: { [key: string]: boolean } = {};
	Object.entries(self.createRights as { [key: string]: string }).forEach(([type, value]) => {
		createRights[type] =
			value === 'ALL' ||
			(value === 'OWN' && accountOwner) ||
			(value === 'OWN' &&
				['Activity', 'Appointment', 'Opportunity', 'Order', 'Agreement', 'Contact'].includes(type));
	});

	account.createRights = createRights;
	return account;
};

type Options = { [key: string]: any };

class SoliditetClientResource extends Resource {
	// The base is untyped .js, so the fields it reads off `this` need declaring here.
	eventName: string;

	constructor() {
		super('soliditet', Attributes);

		this.eventName = 'account';

		this.notifications = {
			buy: () => {
				// The Angular createSuccessBody decremented the credit counter as a side effect of
				// rendering the toast. Kept here, since the counter is what tells the user how many
				// buys they have left and nothing else updates it after a purchase.
				const metadata = AppService.getMetadata();
				metadata.soliditetCredits -= 1;
				AppService.setMetadata(metadata);

				return {
					title: 'default.accountCreated',
					body: `${metadata.soliditetCredits} ${T('ads.yourCredits').toLowerCase()}`,
					style: 'success',
					icon: 'check',
					type: 'body'
				};
			},
			buyError: () => ({
				title: 'default.error',
				body: 'saveError.account',
				style: 'error',
				icon: 'times',
				type: 'body'
			}),
			refresh: () => ({
				title: 'default.updated',
				body: 'updated.account',
				style: 'success',
				icon: 'check',
				type: 'body'
			}),
			refreshError: () => ({
				title: 'default.error',
				body: 'saveError.account',
				style: 'error',
				icon: 'times',
				type: 'body'
			}),
			updateMatchesError: () => ({
				title: 'default.error',
				body: 'saveError.account',
				style: 'error',
				icon: 'times',
				type: 'body'
			})
		} as any;
	}

	customer(customerId: number | string) {
		const soliditet = `${customerId}/soliditet`;

		return {
			find: (filter: Options, options: Options = {}) =>
				this._getRequest('clients', {
					...options,
					_url: soliditet,
					methodName: 'find',
					params: { ...filter, ...options }
				}).then((res: { data: any }) => res.data),

			findMatches: (filter: Options, options: Options = {}) =>
				this._getRequest('matcher', {
					...options,
					_url: soliditet,
					methodName: 'findMatches',
					params: { ...filter, ...options }
				}).then((res: { data: any }) => res.data),

			searchMatches: (filter: Options, options: Options = {}) =>
				this._getRequest('matcher/search', {
					...options,
					_url: soliditet,
					methodName: 'searchMatches',
					params: { ...filter, ...options }
				}).then((res: { data: any }) => res.data),

			buyPreview: (filters: Options, properties: Options, options: Options = {}) =>
				this._postRequest(
					'search/buy',
					{ filters, properties, options },
					{ _url: soliditet, methodName: 'buyPreview' }
				).then((res: { data: any }) => res.data),

			buy: (duns: number | string, properties: Options, options: Options = {}) =>
				this._postRequest(
					'clients',
					{ duns, properties, options: options || {} },
					{ ...options, _url: soliditet, methodName: 'buy' }
				).then((res: { data: any }) => {
					const parsed = { ...res.data, data: parseAccount(res.data.data) };
					this._triggerEvent(options, 'added', parsed);
					return parsed;
				}),

			refresh: (clientId: number, properties: Options, options: Options = {}, opts: Options = {}) =>
				this._putRequest(
					`clients/${clientId}`,
					{ properties, options: options || {} },
					{ ...opts, _url: soliditet, methodName: 'refresh' }
				).then((res: { data: any }) => {
					const parsed = { ...res.data, data: parseAccount(res.data.data) };
					this._triggerEvent(opts, 'updated', parsed);
					return parsed;
				}),

			updateMatches: (filter: Options, options: Options = {}) =>
				this._putRequest(
					'matcher',
					{ ...filter, ...options },
					{
						...options,
						_url: soliditet,
						methodName: 'updateMatches'
					}
				).then((res: { data: any }) => {
					// The Angular instance broadcast this itself rather than through Resource._triggerEvent,
					// and Matcher listens for it to refresh its list. Angular's $promise resolved
					// with the response body, not the whole response.
					EventBus.broadcast('matcher.updated', res.data);
					return res.data;
				})
		};
	}
}

export default new SoliditetClientResource();
